import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";
import { useLang, lp } from "@/lib/i18n";
import { ASSETS } from "@/assets";

const T = {
  fr: {
    seoTitle: "La Maison — Maison Partita",
    seoDesc: "Une suite de collections régies chacune par sa propre règle. La transmutation, les deux voies, les essences rares, la fabrication en petites séries.",
    kicker: "La Maison",
    title: "La transmutation",
    intro: <>
      <em>Partita</em> a deux sens en italien, et la maison tient dans les deux. Une partie,
      une manche — un système clos, des règles, des pièces qui se répondent. Et, en musique,
      une suite : un ensemble de mouvements liés par une même forme. Maison Partita est
      exactement cela : une suite de collections, chacune régie par sa propre règle.
    </>,
    filmCaption: "Film de la maison · lecture à la demande · sous-titres français en option",
    fireWay: "La voie du feu",
    fireText: "Sept métaux, sept astres. Du plomb de Saturne à l'or du Soleil, chaque référence porte le glyphe de sa planète, tracé dans son métal. Sept eaux de parfum pour les sept étapes de l'Œuvre.",
    balanceWay: "La voie de l'équilibre",
    balanceText: "Vingt-quatre tuiles, deux séries en miroir. On ajuste : une tuile tient debout sur sa tranche parce que quelqu'un a mis exactement la bonne force au bon endroit. Trop peu, elle ne tient pas ; trop, elle tombe.",
    discover: "Découvrir →",
    essencesTitle: "Les essences rares",
    essences: [
      "Iris pallida de Toscane, récolté après six ans.",
      "Safran de la Mancha, cueilli à la main avant l'aube.",
      "Ambre gris flotté, patiné par la mer.",
      "Bois de cèdre de l'Atlas, cœur de grume.",
      "Résine d'encens d'Oman, larme claire.",
      "Musc blanc, note de fond tenue et sourde.",
    ],
    smallSeries: "La maison produit en très petites séries. Quatre références seulement sont disponibles aujourd'hui ; le reste de chaque collection est montré, épuisé, parce que montrer la collection entière est le principe même de ces deux maisons.",
  },
  en: {
    seoTitle: "The House — Maison Partita",
    seoDesc: "A suite of collections, each governed by its own rule. Transmutation, the two ways, rare essences, small-series craftsmanship.",
    kicker: "The House",
    title: "Transmutation",
    intro: <>
      <em>Partita</em> carries two meanings in Italian, and the house lives in both. A game,
      a round — a closed system, rules, pieces answering one another. And, in music,
      a suite: a set of movements bound by a single form. Maison Partita is
      exactly that: a suite of collections, each governed by its own rule.
    </>,
    filmCaption: "House film · plays on demand · French subtitles available",
    fireWay: "The way of fire",
    fireText: "Seven metals, seven stars. From Saturn's lead to the Sun's gold, each reference carries the glyph of its planet, struck in its metal. Seven eaux de parfum for the seven stages of the Work.",
    balanceWay: "The way of balance",
    balanceText: "Twenty-four tiles, two mirrored series. It is adjusted into being: a tile stands on its edge because someone applied exactly the right force in exactly the right place. Too little, it will not stand; too much, it falls.",
    discover: "Discover →",
    essencesTitle: "Rare essences",
    essences: [
      "Iris pallida from Tuscany, harvested after six years.",
      "La Mancha saffron, picked by hand before dawn.",
      "Drifted ambergris, weathered by the sea.",
      "Atlas cedarwood, heart of the log.",
      "Omani frankincense resin, a clear tear.",
      "White musk, a held, muted base note.",
    ],
    smallSeries: "The house produces in very small series. Only four references are available today; the rest of each collection is shown, sold out, because showing the entire collection is the very principle of these two houses.",
  },
};

export default function Maison() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);

  return (
    <div>
      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-28 pb-20">
        <Reveal>
          <p className="text-[13px] tracking-[0.34em] uppercase text-zinc-500">{t.kicker}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-[0.08em] text-zinc-50 leading-[1.1]">{t.title}</h1>
          <p className="mt-10 text-[20px] leading-[1.85] text-zinc-300">{t.intro}</p>
        </Reveal>
      </section>

      {/* Film de maison — lecture à la demande */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-10 pb-24">
        <Reveal>
          <video className="w-full bg-zinc-900 border border-zinc-800" style={{ aspectRatio: "16/9" }}
            controls playsInline preload="none" poster={ASSETS.maison} data-testid="maison-video" />
          <p className="mt-4 text-[13px] tracking-[0.16em] uppercase text-zinc-600">{t.filmCaption}</p>
        </Reveal>
      </section>

      {/* Les deux voies */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 pb-24 grid gap-12 md:grid-cols-2">
        <Reveal>
          <div className="border-l pl-8" style={{ borderColor: "#B08D4A" }}>
            <p className="text-[13px] tracking-[0.28em] uppercase" style={{ color: "#B08D4A" }}>{t.fireWay}</p>
            <h2 className="mt-4 text-3xl tracking-[0.14em] uppercase text-zinc-100">Crisopea</h2>
            <p className="mt-5 text-[18px] leading-relaxed text-zinc-300">{t.fireText}</p>
            <Link to={lp(lang, "/crisopea")} className="val-link underline mt-6 inline-block text-[15px]" data-testid="maison-to-crisopea">{t.discover}</Link>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="border-l border-zinc-700 pl-8">
            <p className="text-[13px] tracking-[0.28em] uppercase text-zinc-400">{t.balanceWay}</p>
            <h2 className="mt-4 text-3xl tracking-[0.14em] uppercase text-zinc-100">Domino</h2>
            <p className="mt-5 text-[18px] leading-relaxed text-zinc-300">{t.balanceText}</p>
            <Link to={lp(lang, "/domino")} className="val-link underline mt-6 inline-block text-[15px]" data-testid="maison-to-domino">{t.discover}</Link>
          </div>
        </Reveal>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-10 pb-32">
        <Reveal>
          <h2 className="text-2xl tracking-[0.18em] uppercase text-zinc-100 mb-10">{t.essencesTitle}</h2>
          <ul className="space-y-5">
            {t.essences.map((e) => (
              <li key={e} className="text-[18px] leading-relaxed text-zinc-300 border-l border-zinc-800 pl-6">{e}</li>
            ))}
          </ul>
          <p className="mt-12 text-[17px] leading-[1.8] text-zinc-500">{t.smallSeries}</p>
        </Reveal>
      </section>
    </div>
  );
}
